// src/components/commandSearch/CommandSearchDialog.jsx
//
// Command Search Dialog
// =====================
//
// PURPOSE
// -------
// Renders the central Command Search overlay.
//
// CURRENT BEHAVIOR
// ----------------
// - searches static command registry,
// - optionally queries backend global search when the query is non-empty,
// - groups results visually,
// - supports keyboard navigation,
// - executes results by navigating to their `path`.
//
// BACKEND STRATEGY
// ----------------
// Backend search is already represented here, but static commands remain the
// stable fallback. This allows the extraction to be merged safely before the
// backend endpoint exists.
//
// Once the backend endpoint is ready, the dialog will automatically show backend
// results for typed queries. If the endpoint is temporarily unavailable, the
// static launcher still works.
//
// IMPORTANT SECURITY NOTE
// -----------------------
// Backend must enforce permissions and return only allowed results.
// Frontend role filtering is UX only.
//
// I18N STRATEGY
// -------------
// Static frontend commands are localized before filtering/grouping. This makes
// translated titles and subtitles searchable without changing the backend search
// contract. Backend-provided results stay payload-owned: their title/subtitle
// strings are displayed as returned by the backend because they can contain real
// business record names and dynamic server-generated descriptions.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { COMMAND_ITEMS, localizeCommandItems } from "./commandRegistry";
import { fetchGlobalCommandSearchResults } from "./commandSearchService";
import {
  filterStaticCommands,
  groupCommandResults,
  normalizeSearchText,
} from "./commandSearchUtils";
import { isRequestCanceled } from "../../services/httpUtils";

import "./CommandSearch.css";

const BACKEND_SEARCH_DEBOUNCE_MS = 250;
const MIN_BACKEND_QUERY_LENGTH = 2;

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

function resultKey(item) {
  return `${item.source || "static"}:${item.id || item.path || item.title}`;
}

function dedupeKey(item) {
  /*
    Deduplication key intentionally ignores source.

    Why:
    - static frontend commands and backend static commands can describe the same
      target page/action,
    - for the user this should appear only once,
    - backend business records still remain unique because their paths contain ids,
      e.g. /catalog/products/123 or /vouchers/15.
  */
  const path = String(item?.path || item?.action?.path || "").trim();

  if (path) {
    return `path:${path}`;
  }

  return [
    String(item?.type || "result").trim().toLowerCase(),
    String(item?.group || "results").trim().toLowerCase(),
    String(item?.title || "").trim().toLowerCase(),
    String(item?.subtitle || "").trim().toLowerCase(),
  ].join(":");
}

function buildResultTypeBadgeClassName(type) {
  /*
    The visible label is translated through i18n, but the CSS modifier is based
    on the stable technical result type. This lets us give "page", "report" and
    "action" different visual accents without coupling CSS to the translated
    words "Page" / "Strona", "Report" / "Raport" or "Action" / "Akcja".

    Backend search may later introduce additional result types. Unknown values
    are sanitized and get the neutral fallback styling from the base badge class.
  */
  const normalizedType = String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return [
    "command-search-result__typeBadge",
    normalizedType ? `command-search-result__typeBadge--${normalizedType}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function mergeResults(staticResults, backendResults, query) {
  const q = normalizeSearchText(query);

  /*
    Empty query behaves like a launcher / suggestions view.

    Backend search is intentionally not called for empty query, so only static
    frontend commands are shown.
  */
  if (!q) return staticResults;

  const seen = new Set();
  const merged = [];

  /*
    Static command results come first.

    This is deliberate:
    - they are instant,
    - their paths are known and stable,
    - they work even if backend search is temporarily unavailable.
  */
  for (const item of staticResults) {
    const normalizedItem = { ...item, source: item.source || "static" };
    const key = dedupeKey(normalizedItem);

    if (seen.has(key)) continue;

    seen.add(key);
    merged.push(normalizedItem);
  }

  /*
    Backend results come second.

    Important:
    Deduplication uses path/title semantics, not source.

    Without this, the same command appears twice:
    - once from frontend commandRegistry.js,
    - once from backend globalSearchService.js static commands.
  */
  for (const item of backendResults) {
    const normalizedItem = { ...item, source: item.source || "backend" };
    const key = dedupeKey(normalizedItem);

    if (seen.has(key)) continue;

    seen.add(key);
    merged.push(normalizedItem);
  }

  return merged;
}

export default function CommandSearchDialog({ open, onClose, user }) {
  const navigate = useNavigate();
  const { t } = useTranslation("commandSearch");

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [backendResults, setBackendResults] = useState([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendError, setBackendError] = useState("");

  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const debouncedQuery = useDebouncedValue(query, BACKEND_SEARCH_DEBOUNCE_MS);

  const localizedCommandItems = useMemo(() => {
    return localizeCommandItems(COMMAND_ITEMS, t);
  }, [t]);

  const staticResults = useMemo(() => {
    return filterStaticCommands(localizedCommandItems, query, user);
  }, [localizedCommandItems, query, user]);

  const results = useMemo(() => {
    return mergeResults(staticResults, backendResults, query);
  }, [staticResults, backendResults, query]);

  const groups = useMemo(() => groupCommandResults(results), [results]);

  const close = useCallback(() => {
    abortRef.current?.abort?.();
    abortRef.current = null;

    setQuery("");
    setActiveIndex(0);
    setBackendResults([]);
    setBackendLoading(false);
    setBackendError("");

    onClose?.();
  }, [onClose]);

  const executeResult = useCallback(
    (item) => {
      if (!item) return;

      /*
        Result execution strategy
        -------------------------
        Command Search supports two result families:

        1) Static commands / pages
          Example:
            { path: "/catalog/products" }

        2) Backend business records
          Example:
            { path: "/vouchers/123" }

        The frontend accepts several path aliases defensively because backend
        search may evolve over time:
        - item.path
        - item.to
        - item.url
        - item.action.path
        - item.action.to

        The preferred field is still:
          path
      */
      const action = item.action || null;

      const path =
        item.path ||
        item.to ||
        item.url ||
        action?.path ||
        action?.to ||
        "";

      if (!path) {
        return;
      }

      close();

      navigate(path, action?.state ? { state: action.state } : undefined);
    },
    [close, navigate]
  );

  useEffect(() => {
    if (!open) return;

    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  /**
   * Backend global search.
   *
   * Rules:
   * - empty query: no backend call, show static launcher commands,
   * - short query: no backend call, avoid noisy / expensive search,
   * - query >= MIN_BACKEND_QUERY_LENGTH: call backend,
   * - abort previous request on every new query.
   */
  useEffect(() => {
    if (!open) return undefined;

    const q = String(debouncedQuery || "").trim();

    abortRef.current?.abort?.();
    abortRef.current = null;

    if (q.length < MIN_BACKEND_QUERY_LENGTH) {
      setBackendResults([]);
      setBackendLoading(false);
      setBackendError("");
      return undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setBackendLoading(true);
    setBackendError("");

    fetchGlobalCommandSearchResults(q, { signal: controller.signal })
      .then((rows) => {
        if (controller.signal.aborted) return;
        setBackendResults(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        if (isRequestCanceled(error) || controller.signal.aborted) return;

        // Non-fatal: static commands remain usable.
        setBackendResults([]);
        setBackendError(t("dialog.backendUnavailable"));
      })
      .finally(() => {
        if (!controller.signal.aborted) setBackendLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [open, debouncedQuery, t]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const onInputKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) =>
        Math.min(prev + 1, Math.max(0, results.length - 1))
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(0, prev - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      executeResult(results[activeIndex]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="command-search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("dialog.ariaLabel")}
      onMouseDown={(event) => {
        if (event.target.classList.contains("command-search-overlay")) {
          close();
        }
      }}
    >
      <div
        className="command-search-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="command-search__closeButton"
          onClick={close}
          aria-label={t("dialog.closeAria")}
          title={t("dialog.closeTitle")}
        >
          ×
        </button>

        {backendLoading ? (
          <span className="command-search__topStatus">
            {t("dialog.searching")}
          </span>
        ) : (
          <kbd className="command-search__escHint">Esc</kbd>
        )}

        <div className="command-search-row">
          <span className="command-search-row__icon" aria-hidden="true">
            ⌕
          </span>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t("dialog.placeholder")}
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        {backendError ? (
          <div className="command-search-warning" role="status">
            {backendError}
          </div>
        ) : null}

        <div className="command-search-results" role="listbox">
          {results.length === 0 ? (
            <div className="command-search-empty">
              {t("dialog.noResults", { query })}
            </div>
          ) : (
            groups.map((group) => {
              const groupLabel =
                group.name === "Results" ? t("groups.results") : group.name;

              return (
              <section
                className="command-search-group"
                key={group.name}
                aria-label={groupLabel}
              >
                <h3>{groupLabel}</h3>

                {group.items.map((item) => {
                  const flatIndex = results.findIndex(
                    (x) => resultKey(x) === resultKey(item)
                  );

                  return (
                    <button
                      key={resultKey(item)}
                      type="button"
                      className={[
                        "command-search-result",
                        flatIndex === activeIndex ? "is-active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onClick={() => executeResult(item)}
                      role="option"
                      aria-selected={flatIndex === activeIndex}
                    >
                      <span className="command-search-result__main">
                        <strong>{item.title}</strong>
                        {item.subtitle ? <em>{item.subtitle}</em> : null}
                      </span>

                      <span className="command-search-result__meta">
                        {item.type ? (
                          <span className={buildResultTypeBadgeClassName(item.type)}>
                            {t(`resultTypes.${item.type}`, {
                              defaultValue: item.type,
                            })}
                          </span>
                        ) : null}
                        <span aria-hidden="true">↵</span>
                      </span>
                    </button>
                  );
                })}
              </section>
              );
            })
          )}
        </div>

        <div className="command-search-footer">
          <span>{t("footer.navigate")}</span>
          <span>{t("footer.open")}</span>
          <span>{t("footer.close")}</span>
        </div>
      </div>
    </div>
  );
}
